import { formatCurrency, pluralize } from "@gymflow/helpers";
import { SessionPackDTO } from "@gymflow/types";
import { useClubSettings } from "apps/portal/src/providers";
import React from "react";
import { useTranslation } from "react-i18next";

import { Button, ClockIcon, CreditsIcon, InfinityIcon } from "../../../atoms";
import { HostedPagesCard } from "../../components/HostedPagesCard";

export type HostedPagesCreditPackCardProps = {
  creditPack: SessionPackDTO;
  activeSubscription: boolean;
};

export const HostedPagesCreditPackCard: React.FC<
  HostedPagesCreditPackCardProps
> = ({ creditPack, activeSubscription }) => {
  const { t } = useTranslation();
  const {
    club: { defaultCurrency },
  } = useClubSettings();

  const defaultPrice =
    creditPack.price === 0
      ? t("portal.common.free")
      : formatCurrency(creditPack.price, defaultCurrency);

  const membersPrice =
    creditPack.membersOnlyPrice?.price === undefined
      ? undefined
      : creditPack.membersOnlyPrice.price === 0
        ? t("portal.common.free")
        : formatCurrency(
            creditPack.membersOnlyPrice.price || 0,
            defaultCurrency,
          );

  return (
    <HostedPagesCard.Container>
      <HostedPagesCard.Header>
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="dark:text-gray-0 flex-wrap font-bold text-gray-950">
            {creditPack.name}
          </div>
          <div className="flex flex-row gap-2">
            {creditPack.sessionsUnlimited ? (
              <InfinityIcon className="stroke-gray-950 dark:stroke-gray-50" />
            ) : (
              <div className="dark:text-gray-0 font-bold text-gray-950">
                {creditPack.sessionsIncluded}
              </div>
            )}
            <CreditsIcon pathClassName="stroke-secondary-500" />
          </div>
        </div>
        <HostedPagesCard.Description description={creditPack.description} />
      </HostedPagesCard.Header>
      <HostedPagesCard.Separator />
      <HostedPagesCard.Footer>
        <HostedPagesCard.Price
          price={
            activeSubscription && membersPrice !== undefined
              ? membersPrice
              : defaultPrice
          }
          alternativePrice={
            activeSubscription && membersPrice !== undefined
              ? t("portal.common.priceForNonMembers", { price: defaultPrice })
              : membersPrice
                ? t("portal.common.priceForMembers", { price: membersPrice })
                : undefined
          }
          description={
            <>
              <ClockIcon />
              {creditPack.expiryType === "NA"
                ? "No expiry"
                : creditPack.expiryType === "WEEKLY"
                  ? `${creditPack.expiryValue} ${pluralize(
                      "Week",
                      "Weeks",
                      creditPack.expiryValue,
                    )} Expiry`
                  : `${creditPack.expiryValue} ${pluralize(
                      "Month",
                      "Months",
                      creditPack.expiryValue,
                    )} Expiry`}
            </>
          }
        />

        <Button
          link={`${document.location.pathname}/${creditPack.id}`}
          intent="secondary-outline"
          className="w-fit"
        >
          {t("portal.common.viewMore")}
        </Button>
      </HostedPagesCard.Footer>
    </HostedPagesCard.Container>
  );
};
